/**
 * Topological sort — Kahn's algorithm.
 *
 * Ported from `gantt/sources/core/relations/graph_helper.js`'s
 * `topologicalSort()`. Same observable behavior, modernized in TypeScript:
 *  - Uses Map<TaskID, NodeRec> instead of object-as-hash so TaskID supports
 *    both string and number without coercion bugs.
 *  - Returns `null` on cycle (cycles are caught by SCC in `scc.ts`; the v1
 *    code returned a partial list silently — that's a v1 quirk we don't
 *    inherit).
 *
 * Time complexity: O(V + E).
 */

import type { NormalizedTaskID } from "../types";

interface Edge {
  source: NormalizedTaskID;
  target: NormalizedTaskID;
}

interface NodeRec {
  id: NormalizedTaskID;
  outgoing: number[]; // indices into `edges`
  incoming: number;
}

/**
 * Returns a topological ordering of node IDs, or `null` if the graph
 * contains a cycle (some nodes remained with non-zero in-degree).
 *
 * Vertex set is derived from the edges. Disconnected nodes that don't
 * appear in any edge are not in the result — callers wanting full coverage
 * should pass an explicit nodeIds set (see `topologicalSortWithNodes`).
 */
export function topologicalSort(edges: readonly Edge[]): readonly NormalizedTaskID[] | null {
  const vertices = collectVertices(edges);
  return runKahn(vertices, edges);
}

/**
 * Variant that accepts an explicit vertex set. Useful when the graph has
 * disconnected nodes that have no incident edges but must still appear in
 * the toposort output (e.g., the analysis graph includes every task,
 * including ones with no links).
 */
export function topologicalSortWithNodes(
  nodeIds: Iterable<NormalizedTaskID>,
  edges: readonly Edge[]
): readonly NormalizedTaskID[] | null {
  const vertices = new Set<NormalizedTaskID>(nodeIds);
  // Edges may reference vertices not yet in the set (defensive); add them.
  for (const e of edges) {
    vertices.add(e.source);
    vertices.add(e.target);
  }
  return runKahn(vertices, edges);
}

/**
 * Returns the set of distinct vertex IDs appearing in any edge.
 */
export function collectVertices(edges: readonly Edge[]): Set<NormalizedTaskID> {
  const set = new Set<NormalizedTaskID>();
  for (const e of edges) {
    set.add(e.source);
    set.add(e.target);
  }
  return set;
}

function runKahn(vertices: ReadonlySet<NormalizedTaskID>, edges: readonly Edge[]): NormalizedTaskID[] | null {
  const nodes = new Map<NormalizedTaskID, NodeRec>();
  for (const id of vertices) {
    nodes.set(id, { id, outgoing: [], incoming: 0 });
  }

  for (let i = 0; i < edges.length; i++) {
    const e = edges[i];
    const src = nodes.get(e.source);
    const tgt = nodes.get(e.target);
    // Edges referencing vertices not in `vertices` are skipped — callers
    // are expected to filter, but the engine is defensive.
    if (!src || !tgt) continue;
    src.outgoing.push(i);
    tgt.incoming++;
  }

  // Stack of source nodes (no incoming edges).
  const ready: NormalizedTaskID[] = [];
  for (const node of nodes.values()) {
    if (node.incoming === 0) ready.push(node.id);
  }

  const sorted: NormalizedTaskID[] = [];
  while (ready.length > 0) {
    const id = ready.pop() as NormalizedTaskID;
    sorted.push(id);
    const node = nodes.get(id)!;
    for (const edgeIdx of node.outgoing) {
      const successor = nodes.get(edges[edgeIdx].target)!;
      successor.incoming--;
      if (successor.incoming === 0) {
        ready.push(successor.id);
      }
    }
  }

  // If we visited fewer nodes than the vertex set holds, there's a cycle.
  if (sorted.length < nodes.size) return null;
  return sorted;
}
